import { useState, useEffect, useRef } from "react";
import {
  getVoices,
  cloneVoice,
  previewVoice,
  generateTemplate,
} from "../api/AIStudioApi";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkles, Mail, MessageSquare, Phone, Save, Copy, Loader2, Mic, Upload, Play, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const SCENARIO_EXAMPLES = [
  "Microsoft 365 storage full — target IT users",
  "Amazon refund scam targeting Finance managers",
  "CEO gift card fraud for admin assistants",
  "DHL delivery failed — general workforce",
  "MFA bypass QR-code scam for engineers",
  "IRS tax refund — target US-based employees",
];

const AIStudio = () => {
  const [scenario, setScenario] = useState("");
  const [targetRole, setTargetRole] = useState("Finance Manager");
  const [tone, setTone] = useState("urgent");
  const [channels, setChannels] = useState({ email: true, sms: true, whatsapp: true });
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  // Voice Library state
  const [voices, setVoices] = useState([]);
  const [voicesConfigured, setVoicesConfigured] = useState(false);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [cloneDesc, setCloneDesc] = useState("");
  const [cloneFile, setCloneFile] = useState(null);
  const [cloneBusy, setCloneBusy] = useState(false);
  const cloneFileRef = useRef(null);
  const [ttsBusyId, setTtsBusyId] = useState(null);
  const [ttsPreviewText, setTtsPreviewText] = useState("Hello, this is a test of your cloned voice for our security awareness program.");
  const audioRef = useRef(null);

  const fetchVoices = async () => {
    setVoicesLoading(true);
    try {
      const res = await getVoices();
      setVoices(res.data.voices || []);
      setVoicesConfigured(!!res.data.configured);
    } catch (e) {
      toast.error("Failed to load voices");
    }
    setVoicesLoading(false);
  };

  useEffect(() => { fetchVoices(); }, []);

  const handleClone = async () => {
    if (!cloneName.trim() || !cloneFile) {
      toast.error("Provide a name and an audio sample");
      return;
    }
    if (!cloneFile.type.startsWith("audio/")) {
      toast.error("File must be an audio file (mp3, wav, m4a, etc.)");
      return;
    }
    setCloneBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", cloneName);
      fd.append("description", cloneDesc);
      fd.append("file", cloneFile);
      const res = await cloneVoice(fd);
      if (res.data.mocked) {
        toast.warning(`Mock clone created (voice_id=${res.data.voice_id}). Set ELEVENLABS_API_KEY in Settings to enable real cloning.`);
      } else {
        toast.success(`Cloned "${cloneName}" successfully`);
      }
      setCloneName(""); setCloneDesc(""); setCloneFile(null);
      if (cloneFileRef.current) cloneFileRef.current.value = "";
      fetchVoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cloning failed");
    }
    setCloneBusy(false);
  };

  const handlePreviewTts = async (voice) => {
    setTtsBusyId(voice.voice_id);
    try {
      const res = await previewVoice({
        text: ttsPreviewText,
        voice_id: voice.voice_id,
        output_format: "mp3_44100_128",
      });
      if (!res.data.success) {
        toast.warning(res.data.message || "TTS unavailable — set ELEVENLABS_API_KEY");
        return;
      }
      const src = `data:${res.data.content_type};base64,${res.data.audio_b64}`;
      if (audioRef.current) {
        audioRef.current.src = src;
        audioRef.current.play();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    }
    setTtsBusyId(null);
  };

  const handleGenerate = async (save = false) => {
    if (!scenario.trim()) { toast.error("Enter a scenario first"); return; }
    if (save) setSaving(true); else setLoading(true);
    try {
      const selected = Object.keys(channels).filter((c) => channels[c]);
      if (selected.length === 0) { toast.error("Select at least one channel"); return; }
      const res = await generateTemplate({
        scenario,
        target_role: targetRole,
        tone,
        channels: selected,
        save,
        model,
      });
      setResult(res.data.result);
      if (save && res.data.saved_template_ids?.length) {
        toast.success(`Saved ${res.data.saved_template_ids.length} template(s) to your library`);
      } else if (!save) {
        toast.success("Template generated");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI generation failed");
    }
    setLoading(false);
    setSaving(false);
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ai-studio-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1f1f23] flex items-center gap-2">
            <Sparkles size={24} className="text-[#8c3add]" /> AI Studio
          </h1>
          <p className="text-gray-500 text-sm mt-1">Generate multi-channel phishing templates &amp; clone voices for vishing</p>
        </div>
        <Badge className="bg-[#8c3add]/10 text-[#8c3add] gap-1">
          <Sparkles size={12} /> Powered by Claude &amp; ElevenLabs
        </Badge>
      </div>

      <Tabs defaultValue="templates" className="w-full">
        <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-2 sm:grid-cols-2">
          <TabsTrigger value="templates" data-testid="tab-templates">
            <Sparkles size={14} className="mr-1" /> Template Generator
          </TabsTrigger>
          <TabsTrigger value="voices" data-testid="tab-voices">
            <Mic size={14} className="mr-1" /> Voice Library
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Config */}
            <Card className="border border-gray-100 shadow-sm">
              <CardHeader><CardTitle className="text-lg">Describe the scenario</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Scenario prompt</Label>
                  <Textarea
                    value={scenario}
                    onChange={(e) => setScenario(e.target.value)}
                    placeholder="e.g. Amazon refund scam targeting Finance managers with urgency"
                    rows={4}
                    data-testid="scenario-input"
                  />
                  <div className="flex flex-wrap gap-2 pt-1">
                    {SCENARIO_EXAMPLES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="text-[11px] px-2 py-1 border border-gray-200 rounded-full hover:bg-gray-50 text-gray-600"
                        onClick={() => setScenario(s)}
                      >{s}</button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Target Role</Label>
                    <Input value={targetRole} onChange={(e) => setTargetRole(e.target.value)} data-testid="target-role-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tone</Label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger data-testid="tone-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="authoritative">Authoritative</SelectItem>
                        <SelectItem value="conversational">Conversational</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger data-testid="model-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude-sonnet-4-6">Claude Sonnet 4.6 (recommended)</SelectItem>
                      <SelectItem value="claude-opus-4-8">Claude Opus 4.8 (deeper)</SelectItem>
                      <SelectItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (fast)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Channels to generate</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "email", icon: Mail, color: "#8c3add", label: "Email" },
                      { key: "sms", icon: MessageSquare, color: "#1fa7a1", label: "SMS" },
                      { key: "whatsapp", icon: Phone, color: "#22c55e", label: "WhatsApp" },
                    ].map(({ key, icon: Icon, color, label }) => (
                      <label key={key} className="flex items-center justify-between p-3 border rounded-lg cursor-pointer" style={{ backgroundColor: channels[key] ? `${color}10` : "" }}>
                        <div className="flex items-center gap-2">
                          <Icon size={16} style={{ color }} />
                          <span className="text-sm font-medium">{label}</span>
                        </div>
                        <Switch checked={channels[key]} onCheckedChange={(v) => setChannels({ ...channels, [key]: v })} data-testid={`channel-toggle-${key}`} />
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={() => handleGenerate(false)} disabled={loading || saving} className="flex-1 bg-[#8c3add] hover:bg-[#702eb0] gap-2" data-testid="generate-btn">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    {loading ? "Generating..." : "Generate"}
                  </Button>
                  <Button onClick={() => handleGenerate(true)} disabled={loading || saving || !result} variant="outline" className="flex-1 gap-2" data-testid="save-btn">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    {saving ? "Saving..." : "Generate + Save"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Right: Result preview */}
            <Card className="border border-gray-100 shadow-sm" data-testid="result-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Preview</span>
                  {result && (
                    <div className="flex gap-1">
                      <Badge>{result.category || "general"}</Badge>
                      <Badge className={
                        result.difficulty === "hard" ? "bg-red-100 text-red-700" :
                          result.difficulty === "medium" ? "bg-amber-100 text-amber-700" :
                            "bg-green-100 text-green-700"
                      }>{result.difficulty || "medium"}</Badge>
                    </div>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!result ? (
                  <div className="p-8 text-center text-gray-400">
                    <Sparkles size={32} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Enter a scenario and click Generate to see AI output</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {result.subject && (
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-gray-500">Email Subject</Label>
                          <button onClick={() => { navigator.clipboard.writeText(result.subject); toast.success("Copied"); }}><Copy size={12} className="text-gray-400 hover:text-gray-700" /></button>
                        </div>
                        <p className="font-medium text-sm mt-1">{result.subject}</p>
                      </div>
                    )}
                    {result.html_body && (
                      <div>
                        <Label className="text-xs text-gray-500">Email HTML Preview</Label>
                        <div className="mt-1 border rounded-lg overflow-hidden bg-white">
                          <iframe srcDoc={result.html_body} className="w-full h-64" sandbox="allow-same-origin" title="preview" />
                        </div>
                      </div>
                    )}
                    {result.sms_variant && (
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-gray-500 flex items-center gap-1"><MessageSquare size={12} /> SMS Variant</Label>
                          <button onClick={() => { navigator.clipboard.writeText(result.sms_variant); toast.success("Copied"); }}><Copy size={12} className="text-gray-400 hover:text-gray-700" /></button>
                        </div>
                        <div className="mt-1 p-2 bg-gray-50 rounded text-sm">{result.sms_variant}</div>
                      </div>
                    )}
                    {result.whatsapp_variant && (
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-gray-500 flex items-center gap-1"><Phone size={12} /> WhatsApp Variant</Label>
                          <button onClick={() => { navigator.clipboard.writeText(result.whatsapp_variant); toast.success("Copied"); }}><Copy size={12} className="text-gray-400 hover:text-gray-700" /></button>
                        </div>
                        <div className="mt-1 p-2 bg-gray-50 rounded text-sm">{result.whatsapp_variant}</div>
                      </div>
                    )}
                    {result.landing_page_html && (
                      <div>
                        <Label className="text-xs text-gray-500">Landing Page HTML</Label>
                        <div className="mt-1 border rounded-lg overflow-hidden">
                          <iframe srcDoc={result.landing_page_html} className="w-full h-48" sandbox="allow-same-origin" title="landing" />
                        </div>
                      </div>
                    )}
                    {result.why_it_works && (
                      <div className="p-3 bg-[#8c3add]/5 rounded-lg border border-[#8c3add]/20">
                        <Label className="text-xs text-[#8c3add] font-semibold">Why it works</Label>
                        <p className="text-xs text-gray-700 mt-1">{result.why_it_works}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===================== VOICE LIBRARY ===================== */}
        <TabsContent value="voices" className="mt-4" data-testid="voice-library-tab">
          {!voicesConfigured && (
            <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-2" data-testid="voice-config-warning">
              <AlertCircle size={16} className="text-amber-600 mt-0.5" />
              <div className="text-xs text-amber-800">
                <b>ElevenLabs API key not configured.</b> Cloning &amp; TTS run in mock mode. Add <code className="bg-white px-1 rounded">ELEVENLABS_API_KEY</code> in <b>Settings → Integrations</b> for real voice cloning.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Clone form */}
            <Card className="border border-gray-100 shadow-sm lg:col-span-1" data-testid="voice-clone-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mic size={18} className="text-[#8c3add]" /> Clone a new voice
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label>Voice Name</Label>
                  <Input value={cloneName} onChange={(e) => setCloneName(e.target.value)} placeholder="e.g. CEO John Smith" data-testid="clone-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input value={cloneDesc} onChange={(e) => setCloneDesc(e.target.value)} placeholder="Warm, authoritative, corporate" data-testid="clone-desc-input" />
                </div>
                <div className="space-y-2">
                  <Label>Voice Sample (audio file, ≤25MB, ≥1 min recommended)</Label>
                  <Input ref={cloneFileRef} type="file" accept="audio/*" onChange={(e) => setCloneFile(e.target.files?.[0] || null)} data-testid="clone-file-input" />
                  <p className="text-[11px] text-gray-500">Uses ElevenLabs Instant Voice Cloning. Ensure you have permission to use this voice.</p>
                </div>
                <Button onClick={handleClone} disabled={cloneBusy} className="w-full bg-[#8c3add] hover:bg-[#702eb0] gap-2" data-testid="clone-submit-btn">
                  {cloneBusy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {cloneBusy ? "Cloning…" : "Clone Voice"}
                </Button>
              </CardContent>
            </Card>

            {/* Voice list */}
            <Card className="border border-gray-100 shadow-sm lg:col-span-2" data-testid="voice-list-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2"><Phone size={18} className="text-[#1fa7a1]" /> Voice Library ({voices.length})</span>
                  <Button variant="outline" size="sm" onClick={fetchVoices} disabled={voicesLoading} data-testid="voice-refresh-btn">
                    {voicesLoading ? <Loader2 size={14} className="animate-spin" /> : "Refresh"}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Preview text</Label>
                  <Textarea value={ttsPreviewText} onChange={(e) => setTtsPreviewText(e.target.value)} rows={2} className="text-sm" data-testid="voice-preview-text" />
                </div>
                <audio ref={audioRef} className="w-full hidden" />
                {voicesLoading ? (
                  <div className="p-8 text-center"><Loader2 size={20} className="animate-spin mx-auto text-gray-400" /></div>
                ) : voices.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 text-sm">
                    No voices yet. Clone your first voice on the left.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto pr-1" data-testid="voice-grid">
                    {voices.map((v) => (
                      <div key={v.voice_id} className="border border-gray-100 rounded-lg p-3 flex items-center justify-between gap-2 hover:bg-gray-50 transition" data-testid={`voice-item-${v.voice_id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#1f1f23] truncate">{v.name}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Badge variant="outline" className="text-[10px] capitalize">{v.category || "premade"}</Badge>
                            {v.mocked && <Badge className="text-[10px] bg-amber-100 text-amber-700">mock</Badge>}
                          </div>
                          <p className="text-[10px] text-gray-400 font-mono truncate mt-1">{v.voice_id}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePreviewTts(v)}
                            disabled={ttsBusyId === v.voice_id}
                            className="h-8 gap-1"
                            data-testid={`voice-preview-${v.voice_id}`}
                          >
                            {ttsBusyId === v.voice_id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                            Preview
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { navigator.clipboard.writeText(v.voice_id); toast.success("Voice ID copied"); }}
                            className="h-8 gap-1 text-[11px]"
                          >
                            <Copy size={12} /> ID
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIStudio;
